<template>
    <div v-if="enabled" class="composer-mentions sr-only">
        <p :id="selectedId">{{ selectedDescription }}</p>
        <div role="status" aria-live="polite" class="sr-only">
            <template v-if="announcement">{{ announcement }}</template>
            <template v-else-if="open">{{
                loading ? t("chat.mentions.loading") : error ? t("chat.mentions.error") : t("chat.mentions.results", { count: suggestions.length })
            }}</template>
        </div>
        <PopoverRoot
            :open="open"
            :modal="false"
            @update:open="
                (value) => {
                    if (!value) dismissed = true;
                }
            ">
            <PopoverAnchor :reference="textarea ?? undefined" />
            <PopoverPortal>
                <PopoverContent
                    :aria-label="teamMentionsEnabled ? t('chat.mentions.title') : t('chat.mentions.peopleTitle')"
                    class="mention-suggestions"
                    side="top"
                    align="start"
                    :side-offset="12"
                    :collision-padding="12"
                    @open-auto-focus.prevent
                    @close-auto-focus.prevent
                    @interact-outside="dismissed = true">
                    <div
                        v-show="flow.step === 'suggestions' && !loading && !error && suggestions.length"
                        :id="listId"
                        role="listbox"
                        :aria-label="teamMentionsEnabled ? t('chat.mentions.title') : t('chat.mentions.peopleTitle')"
                        class="min-h-0 max-h-64 overflow-y-auto p-1">
                        <template v-if="!loading && !error">
                            <button
                                v-for="(suggestion, index) in suggestions"
                                :id="`${listId}-${index}`"
                                :key="`${suggestion.kind}-${suggestion.entity_id}`"
                                role="option"
                                type="button"
                                tabindex="-1"
                                :aria-selected="activeIndex === index"
                                class="mention-option"
                                :class="{ 'bg-surface-hover': activeIndex === index }"
                                @pointerdown.prevent
                                @click="selectSuggestion(suggestion)"
                                @pointermove="activeIndex = index">
                                <span class="flex size-6 shrink-0 items-center justify-center text-ink-muted"
                                    ><i :class="suggestion.kind === 'team' ? 'bi bi-people-fill' : 'bi bi-person-fill'" aria-hidden="true" /><span class="sr-only">{{
                                        mentionKindLabel(suggestion.kind)
                                    }}</span></span
                                >
                                <span class="min-w-0 grow truncate scaffold-body-small" :title="[suggestion.label, suggestionDescription(suggestion)].filter(Boolean).join(' · ')">
                                    <span class="scaffold-body-small-medium">{{ suggestion.label }}</span>
                                    <span v-if="suggestionDescription(suggestion)" class="ml-2 text-ink-muted">{{ suggestionDescription(suggestion) }}</span>
                                </span>
                            </button>
                        </template>
                    </div>
                    <div v-if="flow.step === 'choose' || (!loading && !error && data && !suggestions.length)" ref="choices" class="p-1">
                        <button
                            v-for="kind in creationKinds"
                            :key="kind"
                            type="button"
                            class="mention-option scaffold-body-small"
                            @click="startCreation(kind)"
                            @keydown="chooserKeydown">
                            <i class="bi bi-plus-lg" aria-hidden="true" />{{ kind === "person" ? t("chat.mentions.addPerson") : t("chat.mentions.addTeam") }}
                        </button>
                    </div>
                    <div class="empty:sr-only">
                        <p v-if="loading" class="body-small text-ink-muted px-3 py-4">{{ t("chat.mentions.loading") }}</p>
                        <div v-else-if="error" class="px-3 py-3">
                            <p class="body-small text-ink-muted">{{ t("chat.mentions.error") }}</p>
                            <BaseButton theme="tertiary" size="sm" @click="loadDirectory">{{ t("chat.mentions.retry") }}</BaseButton>
                        </div>
                    </div>
                </PopoverContent>
            </PopoverPortal>
        </PopoverRoot>
        <ComposerMentionCreateDialog
            v-if="flow.step === 'create'"
            :kind="flow.kind"
            :initial-name="flow.origin.query.trim()"
            :return-focus-to="textarea"
            @created="onCreated"
            @cancel="cancelCreation" />
    </div>
</template>

<script setup lang="ts">
import { type AfterFetchContext, type OnFetchErrorContext, useEventListener } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { ComposerMention } from "~vue/api/generated/chat";
import type { MentionSuggestion, MentionSuggestionsResponse } from "~vue/api/generated/connections";
import BaseButton from "~vue/components/BaseButton.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { activeLocale } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import { PopoverAnchor, PopoverContent, PopoverPortal, PopoverRoot } from "reka-ui";
import { computed, nextTick, onBeforeUnmount, ref, useId, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import ComposerMentionCreateDialog from "./ComposerMentionCreateDialog.vue";
import { mentionQuery, reconcileMentions } from "./mentionOccurrences";

const props = defineProps<{ textarea: HTMLTextAreaElement | null | undefined; enabled: boolean; disabled: boolean }>();
const emit = defineEmits<{ "team-created": [] }>();
const text = defineModel<string>("text", { required: true });
const mentions = defineModel<ComposerMention[]>("mentions", { required: true });
const { t } = useI18n();
const listId = useId();
const selectedId = useId();
const choices = useTemplateRef<HTMLElement>("choices");
const caret = ref(0);
const dismissed = ref(false);
const activeIndex = ref(0);
const query = computed(() => {
    if (!props.enabled || props.disabled) {
        return null;
    }
    const range = mentionQuery(text.value, caret.value);
    return range && !mentions.value.some((mention) => mention.start === range.start) ? range : null;
});
type CreationOrigin = { readonly text: string; readonly start: number; readonly end: number; readonly query: string };
type PickerFlow = { step: "suggestions" } | { step: "choose"; origin: CreationOrigin } | { step: "create"; origin: CreationOrigin; kind: MentionSuggestion["kind"] };
const flow = ref<PickerFlow>({ step: "suggestions" });
const announcement = ref("");
const open = computed(() => flow.value.step !== "create" && !!query.value && !dismissed.value);
let disposed = false;
let directoryGeneration = 0;
const {
    data,
    error,
    isFetching: loading,
    execute,
    abort,
} = useFetch("/v1/connections/mention-suggestions", {
    immediate: false,
    refetch: false,
    afterFetch: (context): AfterFetchContext<MentionSuggestionsResponse> => ({
        ...context,
        data: disposed || !props.enabled || context.context.options.signal?.aborted ? data.value : context.data,
    }),
    onFetchError: (context): OnFetchErrorContext<MentionSuggestionsResponse, unknown> =>
        disposed || !props.enabled || context.context.options.signal?.aborted ? { ...context, data: data.value, error: error.value } : context,
})
    .get()
    .json<MentionSuggestionsResponse>();
const teamMentionsEnabled = computed(() => data.value?.team_mentions_enabled === true);
const creationKinds = computed<MentionSuggestion["kind"][]>(() => (teamMentionsEnabled.value ? ["person", "team"] : ["person"]));
const suggestions = computed(() => {
    const terms = (query.value?.query ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matches = (data.value?.suggestions ?? []).filter(
        (suggestion) => creationKinds.value.includes(suggestion.kind) && terms.every((term) => suggestion.label.toLowerCase().includes(term)),
    );
    const people = matches.filter((suggestion) => suggestion.kind === "person");
    const teams = matches.filter((suggestion) => suggestion.kind === "team");
    const results: MentionSuggestion[] = [];
    for (let index = 0; results.length < 20 && (index < people.length || index < teams.length); index++) {
        if (people[index]) {
            results.push(people[index]);
        }
        if (teams[index] && results.length < 20) {
            results.push(teams[index]);
        }
    }
    return results;
});
// The pills are painted, not read, so the textarea describes which names are tagged and to whom they belong.
const selectedDescription = computed(() => {
    if (!mentions.value.length) {
        return "";
    }
    const directory = new Map((data.value?.suggestions ?? []).map((suggestion) => [`${suggestion.kind}:${suggestion.entity_id}`, suggestion]));
    const items = mentions.value.map((mention) => {
        const suggestion = directory.get(`${mention.kind}:${mention.entity_id}`);
        const detail = suggestion ? suggestionDescription(suggestion) : "";
        const kind = mentionKindLabel(mention.kind);
        return detail ? t("chat.mentions.selectedItemWithDetail", { name: mention.label, kind, detail }) : t("chat.mentions.selectedItem", { name: mention.label, kind });
    });
    return t("chat.mentions.selected", { names: new Intl.ListFormat(activeLocale(), { type: "conjunction" }).format(items) });
});
function mentionKindLabel(kind: MentionSuggestion["kind"]) {
    return kind === "person" ? t("chat.mentions.person") : t("chat.mentions.team");
}
function suggestionDescription(suggestion: MentionSuggestion) {
    const description = suggestion.description || "";
    const ambiguous = (data.value?.suggestions ?? []).some(
        (other) => other.entity_id !== suggestion.entity_id && other.kind === suggestion.kind && other.label === suggestion.label && other.description === suggestion.description,
    );
    return ambiguous ? [description, suggestion.entity_id.slice(0, 8)].filter(Boolean).join(" · ") : description;
}
async function loadDirectory() {
    if (props.enabled && !loading.value && (!data.value || error.value)) {
        const generation = directoryGeneration;
        await execute();
        if (!disposed && generation !== directoryGeneration) {
            void loadDirectory();
        }
    }
}
watch(
    () => props.enabled,
    (enabled) => {
        if (enabled) {
            void loadDirectory();
        } else {
            flow.value = { step: "suggestions" };
            announcement.value = "";
            directoryGeneration++;
            abort();
            data.value = null;
            error.value = null;
        }
    },
    { immediate: true },
);
onBeforeUnmount(() => {
    disposed = true;
    abort();
});
watch(
    () => query.value?.query,
    () => {
        activeIndex.value = 0;
        dismissed.value = false;
        if (flow.value.step === "choose") {
            flow.value = { step: "suggestions" };
        }
    },
);
// The picker's own edits reconcile mentions themselves and set an announcement the text watcher must not clear.
let ownEdit: string | null = null;
watch(
    text,
    (after, before) => {
        if (ownEdit === after) {
            ownEdit = null;
            return;
        }
        announcement.value = "";
        mentions.value = reconcileMentions(before, after, mentions.value);
    },
    { flush: "sync" },
);
watch(
    [open, flow, activeIndex, suggestions, () => props.textarea, () => props.enabled, () => mentions.value.length > 0],
    () => {
        const field = props.textarea;
        if (!field) {
            return;
        }
        setDescribedBy(field, props.enabled && mentions.value.length > 0);
        if (!props.enabled) {
            for (const attribute of ["role", "aria-haspopup", "aria-autocomplete", "aria-expanded", "aria-controls", "aria-activedescendant"]) {
                field.removeAttribute(attribute);
            }
            return;
        }
        field.setAttribute("role", "combobox");
        field.setAttribute("aria-haspopup", suggestions.value.length && flow.value.step === "suggestions" ? "listbox" : "dialog");
        field.setAttribute("aria-autocomplete", "list");
        field.setAttribute("aria-expanded", String(open.value));
        if (open.value && suggestions.value.length && flow.value.step === "suggestions") {
            field.setAttribute("aria-controls", listId);
        } else {
            field.removeAttribute("aria-controls");
        }
        if (open.value && flow.value.step === "suggestions" && suggestions.value[activeIndex.value]) {
            field.setAttribute("aria-activedescendant", `${listId}-${activeIndex.value}`);
        } else {
            field.removeAttribute("aria-activedescendant");
        }
        void nextTick(() => document.getElementById(`${listId}-${activeIndex.value}`)?.scrollIntoView?.({ block: "nearest" }));
    },
    { immediate: true },
);
// Keeps any description the textarea already has, adding or removing only the tagged-names one.
function setDescribedBy(field: HTMLTextAreaElement, described: boolean) {
    const ids = (field.getAttribute("aria-describedby") ?? "").split(/\s+/).filter((id) => id && id !== selectedId);
    if (described) {
        ids.push(selectedId);
    }
    if (ids.length) {
        field.setAttribute("aria-describedby", ids.join(" "));
    } else {
        field.removeAttribute("aria-describedby");
    }
}
function syncCaret() {
    caret.value = props.textarea?.selectionStart ?? 0;
}
useEventListener(() => props.textarea, ["input", "click", "keyup", "select", "focus"], syncCaret);
useEventListener(
    () => props.textarea,
    "keydown",
    (event: KeyboardEvent) => {
        if (removeWholeMention(event)) {
            return;
        }
        if (!open.value || event.isComposing || event.keyCode === 229) {
            return;
        }
        if (event.key === "Tab" && !suggestions.value.length) {
            return;
        }
        if (!suggestions.value.length && !loading.value && !error.value) {
            if ((event.key === "Enter" || event.key === "ArrowDown") && data.value) {
                event.preventDefault();
                event.stopImmediatePropagation();
                chooseCreation();
                return;
            }
        }
        if (["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(event.key) && !event.shiftKey) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (event.key === "Escape") {
                dismissed.value = true;
            } else if (event.key === "Enter" || event.key === "Tab") {
                const suggestion = suggestions.value[activeIndex.value];
                if (suggestion && !loading.value && !error.value) {
                    selectSuggestion(suggestion, event.key !== "Tab");
                }
            } else if (suggestions.value.length) {
                activeIndex.value = (activeIndex.value + (event.key === "ArrowDown" ? 1 : -1) + suggestions.value.length) % suggestions.value.length;
            }
        }
    },
    { capture: true },
);
function selectSuggestion(suggestion: MentionSuggestion, appendSpace = true, range = query.value, explicitSelection = true) {
    if (!range) {
        return false;
    }
    const label = `@${suggestion.label}`;
    const completion = label + (appendSpace ? " " : "");
    const nextText = text.value.slice(0, range.start) + completion + text.value.slice(range.end);
    if (props.textarea && props.textarea.maxLength >= 0 && nextText.length > props.textarea.maxLength) {
        return false;
    }
    if (suggestion.kind === "team" && explicitSelection) {
        logUserInteraction(AmplitudeEventNames.TEAM_SELECTED, { source: TEAM_INTERACTION_SOURCE.CHAT_COMPOSER, team_id: suggestion.entity_id });
    }
    const shiftedMentions = reconcileMentions(text.value, nextText, mentions.value);
    ownEdit = nextText;
    text.value = nextText;
    mentions.value = [
        ...shiftedMentions,
        { kind: suggestion.kind, entity_id: suggestion.entity_id, label: suggestion.label, start: range.start, end: range.start + label.length },
    ].sort((a, b) => a.start - b.start);
    dismissed.value = true;
    announcement.value = t("chat.mentions.tagged", { name: suggestion.label });
    void nextTick(() => {
        props.textarea?.focus();
        props.textarea?.setSelectionRange(range.start + completion.length, range.start + completion.length);
        syncCaret();
    });
}
// Editing inside a tag would quietly turn it back into plain text, so Backspace and Delete take the whole tag.
function removeWholeMention(event: KeyboardEvent) {
    const field = props.textarea;
    if (
        !field ||
        props.disabled ||
        event.isComposing ||
        (event.key !== "Backspace" && event.key !== "Delete") ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        field.selectionStart !== field.selectionEnd
    ) {
        return false;
    }
    const caret = field.selectionStart;
    const mention = mentions.value.find((candidate) =>
        event.key === "Backspace" ? candidate.start < caret && caret <= candidate.end : candidate.start <= caret && caret < candidate.end,
    );
    if (!mention) {
        return false;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const nextText = text.value.slice(0, mention.start) + text.value.slice(mention.end);
    mentions.value = reconcileMentions(text.value, nextText, mentions.value);
    ownEdit = nextText;
    text.value = nextText;
    announcement.value = t("chat.mentions.removed", { name: mention.label });
    void nextTick(() => {
        field.setSelectionRange(mention.start, mention.start);
        syncCaret();
    });
    return true;
}
function chooseCreation() {
    if (!query.value || !data.value || loading.value || error.value || suggestions.value.length) {
        return;
    }
    flow.value = { step: "choose", origin: { ...query.value, text: text.value } };
    void nextTick(() => choices.value?.querySelector<HTMLButtonElement>("button")?.focus());
}
function startCreation(kind: MentionSuggestion["kind"]) {
    if (!props.enabled || props.disabled || !creationKinds.value.includes(kind)) {
        return;
    }
    if (flow.value.step === "choose") {
        flow.value = { step: "create", kind, origin: flow.value.origin };
    } else if (flow.value.step === "suggestions" && query.value) {
        flow.value = { step: "create", kind, origin: { ...query.value, text: text.value } };
    }
}
function restoreDraftFocus(origin: CreationOrigin) {
    void nextTick(() => {
        props.textarea?.focus();
        if (text.value === origin.text) {
            props.textarea?.setSelectionRange(origin.end, origin.end);
        }
        syncCaret();
    });
}
function cancelCreation() {
    if (flow.value.step !== "create") {
        return;
    }
    const { origin } = flow.value;
    flow.value = { step: "suggestions" };
    dismissed.value = true;
    restoreDraftFocus(origin);
}
function onCreated(suggestion: MentionSuggestion) {
    if (disposed || !props.enabled || flow.value.step !== "create" || !creationKinds.value.includes(suggestion.kind)) {
        return;
    }
    const { origin } = flow.value;
    if (suggestion.kind === "team") {
        emit("team-created");
    }
    if (data.value) {
        data.value = {
            ...data.value,
            suggestions: [...data.value.suggestions.filter((entry) => entry.kind !== suggestion.kind || entry.entity_id !== suggestion.entity_id), suggestion],
        };
    }
    flow.value = { step: "suggestions" };
    dismissed.value = true;
    if (text.value === origin.text && !props.disabled) {
        selectSuggestion(suggestion, true, origin, false);
    }
    announcement.value = t("chat.mentions.saved", { name: suggestion.label });
    restoreDraftFocus(origin);
}
function chooserKeydown(event: KeyboardEvent) {
    if (!(event.currentTarget instanceof HTMLElement)) {
        return;
    }
    const buttons = [...(event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        flow.value = { step: "suggestions" };
        dismissed.value = true;
        props.textarea?.focus();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const index = buttons.findIndex((button) => button === document.activeElement);
        buttons[(index + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length]?.focus();
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    :global(.mention-suggestions) {
        @apply z-40 flex w-96 max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-xl border border-border bg-surface-raised text-ink shadow-lg;
        max-height: var(--reka-popover-content-available-height);
    }
    .mention-option {
        @apply flex min-h-10 w-full items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-surface focus-visible:outline-control-ring;
    }
}
</style>
